import type { ResolvedNavigationGroup } from "@/config/sidebar-config";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import type { Dispatch, SetStateAction } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../../assets/logo.png";

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  desktopSidebarCollapsed: boolean;
  setDesktopSidebarCollapsed: Dispatch<SetStateAction<boolean>>;
  navigationGroups: ResolvedNavigationGroup[];
  enabledModules?: string[];
}

export function Sidebar({
  sidebarOpen,
  setSidebarOpen,
  desktopSidebarCollapsed,
  setDesktopSidebarCollapsed,
  navigationGroups,
  enabledModules,
}: SidebarProps) {
  const location = useLocation();
  const isCollapsed = desktopSidebarCollapsed;

  return (
    <aside
      className={`fixed lg:relative inset-y-0 left-0 bg-white border-r border-gray-100 shadow-sm z-[70] transition-all duration-300 ease-in-out shrink-0 flex flex-col
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        ${isCollapsed ? "lg:w-20" : "lg:w-64"}
      `}
    >
      {/* Floating Toggle Button (Desktop Only) */}
      <button
        type="button"
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-expanded={!isCollapsed}
        onClick={() => setDesktopSidebarCollapsed((collapsed) => !collapsed)}
        className="hidden lg:flex absolute -right-[18px] top-[18px] z-[80] h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-md transition-all duration-200 hover:border-teal-200 hover:text-teal-700 hover:shadow-lg active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
      >
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4" />
        ) : (
          <ChevronLeft className="w-4 h-4" />
        )}
      </button>

      {/* Logo Section */}
      <div
        className={`flex h-[72px] items-center ${isCollapsed ? "justify-center" : "justify-center"}`}
      >
        <div
          className={`flex items-center overflow-hidden transition-all duration-300 ${isCollapsed ? "w-full justify-center" : "w-auto"}`}
        >
          <img src={logo} alt="Logo" className="w-10 h-10 object-contain" />
          <span
            className={`text-2xl pl-2 font-bold whitespace-nowrap transition-all duration-300 ${isCollapsed ? "w-0 opacity-0 overflow-hidden" : "w-auto opacity-100"}`}
          >
            <span className="text-blue-900">Medi</span>
            <span className="text-red-700">Core</span>
            <span className="text-teal-700">Pay</span>
          </span>
        </div>

        {/* Mobile Close Button */}
        <button
          type="button"
          aria-label="Close sidebar"
          onClick={() => setSidebarOpen(false)}
          className="ml-auto mr-3 flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 lg:hidden"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation - Scrollable Area */}
      <nav className="flex-1 px-4 py-6 space-y-6 overflow-y-auto scrollbar-hide">
        {!enabledModules ? (
          <div className="text-xs text-gray-400 px-4">Loading access…</div>
        ) : (
          navigationGroups.map((group) => {
            return (
              <div key={group.title} className="space-y-1.5">
                {!isCollapsed && (
                  <h3 className="px-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                    {group.title}
                  </h3>
                )}
                {group.items.map((item) => {
                  const isActive = location.pathname.startsWith(item.href);
                  const IconComponent = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center ${isCollapsed ? "justify-center px-0" : "px-4"} py-2.5 rounded-xl transition-all duration-200 group relative
                        ${
                          isActive
                            ? "bg-teal-600 text-white shadow-sm shadow-teal-200"
                            : "text-gray-500 hover:bg-teal-50 hover:text-teal-700"
                        }`}
                      title={isCollapsed ? item.name : undefined}
                    >
                      <IconComponent
                        className={`w-[18px] h-[18px] transition-colors shrink-0 ${isActive ? "text-white" : "text-gray-400 group-hover:text-teal-600"} ${isCollapsed ? "" : "mr-3"}`}
                      />
                      <span
                        className={`text-[13px] font-medium transition-opacity duration-200 whitespace-nowrap ${isCollapsed ? "hidden w-0 opacity-0" : "block opacity-100"}`}
                      >
                        {item.name}
                      </span>
                    </Link>
                  );
                })}
              </div>
            );
          })
        )}
      </nav>
    </aside>
  );
}
